import React, { useCallback, useEffect, useMemo, useRef, } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { useDebouncedCallback } from 'use-debounce';
import { useCarouselListDefaultStyles } from './CarouselListDefault.styles';
import { CarouselPeek } from './model';
export var CarouselListDefault = function (_a) {
    var cards = _a.cards, containerKey = _a.containerKey, cardGutterWidth = _a.cardGutterWidth, carouselBottomMargin = _a.carouselBottomMargin, keyboardNavigationOptions = _a.keyboardNavigationOptions, isRtl = _a.isRtl, itemDimensions = _a.itemDimensions, navigationState = _a.navigationState, resizeState = _a.resizeState, onResize = _a.onResize, renderItem = _a.renderItem, resizeOptions = _a.resizeOptions;
    var styles = useCarouselListDefaultStyles();
    var resizeObserverRef = useRef();
    var containerWidthRef = useRef();
    var containerRef = useRef(null);
    // $TODO: Use isMounted effect
    var isMountedRef = useRef();
    useEffect(function () {
        isMountedRef.current = true;
        return function () {
            isMountedRef.current = false;
        };
    }, []);
    // Updates the container width state after a resize
    var updateContainerSize = useCallback(function (containerWidth) {
        containerWidthRef.current = containerWidth;
        onResize({
            width: containerWidth,
        });
    }, [onResize]);
    var resizeCallback = useDebouncedCallback(function (entries) {
        if (containerRef.current &&
            entries &&
            entries[0] &&
            entries[0].contentRect.width !== containerWidthRef.current &&
            isMountedRef.current) {
            updateContainerSize(containerRef.current.clientWidth);
        }
    }, 10);
    var setContainerRef = useCallback(function (element) {
        if (!element) {
            return;
        }
        // Update refs
        containerRef.current = element;
        if ((keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.containerRef) !== undefined) {
            keyboardNavigationOptions.containerRef.current = element;
        }
        // Set initial container size.
        // resizeCallback below is not immediately called in all cases.
        if (containerWidthRef.current !== containerRef.current.clientWidth) {
            updateContainerSize(containerRef.current.clientWidth);
        }
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
        }
        resizeObserverRef.current = new ResizeObserver(function (entries) {
            return resizeCallback(entries);
        });
        resizeObserverRef.current.observe(element);
    }, [
        keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.containerRef,
        resizeCallback,
        updateContainerSize,
    ]);
    // The memoized item to be displayed for each item
    var getCarouselItem = useCallback(function (index, firstItemIndex) {
        if (index >= cards.length) {
            return null;
        }
        var currentCard = cards[index];
        var relativeIndex = index - firstItemIndex;
        return renderItem(currentCard, index, relativeIndex, keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.itemRefs[index], navigationState.stopIndex);
    }, [
        cards,
        renderItem,
        keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.itemRefs,
        navigationState.stopIndex,
    ]);
    /**
     * Depending on the resize options of the list
     * Update the container styles
     */
    var renderCardStyle = useCallback(function (cardIndex, isRtl) {
        var isLastIndexToBeDrawn = cardIndex === navigationState.stopIndex - 1;
        var flexBasis = itemDimensions.width;
        var marginRight, marginLeft;
        if (isRtl) {
            marginLeft = isLastIndexToBeDrawn ? undefined : cardGutterWidth;
        }
        else {
            marginRight = isLastIndexToBeDrawn
                ? undefined
                : cardGutterWidth;
        }
        var onLastPage = resizeState.pageCount === navigationState.displayedPage;
        if (resizeOptions.type === CarouselPeek.Resize) {
            return {
                style: {
                    flexBasis: flexBasis,
                    flexGrow: onLastPage &&
                        resizeState.cardsOnLastPage <
                            resizeState.cardsPerPage
                        ? 0
                        : 1,
                    marginLeft: marginLeft,
                    marginRight: marginRight,
                    minWidth: 0,
                },
            };
        }
        switch (resizeOptions.type) {
            case CarouselPeek.Fixed:
                if (resizeState.allCardsFit) {
                    return {
                        style: {
                            flexBasis: flexBasis,
                            marginLeft: marginLeft,
                            marginRight: marginRight,
                        },
                    };
                }
                // If we're on the last page, peek the first item.
                // If we're not on the last page, we should peek the last item.
                if ((onLastPage &&
                    cardIndex === navigationState.startIndex) ||
                    (!onLastPage && isLastIndexToBeDrawn)) {
                    return {
                        style: {
                            flexBasis: resizeOptions.peekSizePixels,
                            flexGrow: 0,
                            marginLeft: marginLeft,
                            marginRight: marginRight,
                            maxWidth: resizeOptions.peekSizePixels,
                            width: resizeOptions.peekSizePixels,
                            overflow: 'hidden',
                        },
                        isLastFixedCardToBeDrawn: true,
                    };
                }
                // All other indices should resize except if number of cards on last page are less than number of cards on page.
                return {
                    style: {
                        flexBasis: flexBasis,
                        flexGrow: onLastPage &&
                            resizeState.cardsOnLastPage <
                                resizeState.cardsPerPage
                            ? 0
                            : 1,
                        marginLeft: marginLeft,
                        marginRight: marginRight,
                        minWidth: 0,
                    },
                };
            case CarouselPeek.None:
            default:
                return {
                    style: {
                        marginLeft: marginLeft,
                        marginRight: marginRight,
                        maxWidth: flexBasis,
                        width: flexBasis,
                        overflow: isLastIndexToBeDrawn
                            ? 'hidden'
                            : undefined,
                    },
                };
        }
    }, [
        cardGutterWidth,
        itemDimensions.width,
        navigationState.displayedPage,
        navigationState.startIndex,
        navigationState.stopIndex,
        resizeOptions,
        resizeState.allCardsFit,
        resizeState.cardsOnLastPage,
        resizeState.cardsPerPage,
        resizeState.pageCount,
    ]);
    var renderList = useMemo(function () {
        var _a;
        if (cards.length === 0) {
            return [];
        }
        var renderCards = [];
        for (var cardIndex = navigationState.startIndex; cardIndex < navigationState.stopIndex; cardIndex++) {
            var _b = renderCardStyle(cardIndex, isRtl), style = _b.style, isLastFixedCardToBeDrawn = _b.isLastFixedCardToBeDrawn;
            var carouselItem = getCarouselItem(cardIndex, navigationState.startIndex);
            // Add wrapper to add correct peek behavior on overflow (should look like the card got cut off)
            var carouselItemWrapper = void 0;
            if (isLastFixedCardToBeDrawn) {
                carouselItemWrapper = (React.createElement("div", { style: {
                        width: itemDimensions.width,
                        float: cardIndex === navigationState.startIndex
                            ? isRtl
                                ? 'left'
                                : 'right'
                            : isRtl
                                ? 'right'
                                : 'left',
                    } }, carouselItem));
            }
            else {
                carouselItemWrapper = carouselItem;
            }
            renderCards.push(React.createElement("div", { key: (_a = cards[cardIndex]) === null || _a === void 0 ? void 0 : _a.id, className: styles.item, style: style }, carouselItemWrapper));
        }
        return renderCards;
    }, [
        cards,
        navigationState.startIndex,
        navigationState.stopIndex,
        renderCardStyle,
        isRtl,
        getCarouselItem,
        styles.item,
        itemDimensions.width,
    ]);
    var containerStyle = useMemo(function () { return ({
        marginBottom: carouselBottomMargin,
        height: itemDimensions.height,
    }); }, [carouselBottomMargin, itemDimensions.height]);
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { key: "".concat(navigationState.startIndex, "_").concat(navigationState.stopIndex), className: styles.container, onBlur: keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.onBlur, onKeyDown: keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.handleContainerKeyDown, onFocusCapture: keyboardNavigationOptions === null || keyboardNavigationOptions === void 0 ? void 0 : keyboardNavigationOptions.onFocusCapture, style: containerStyle, ref: setContainerRef, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: keyboardNavigationOptions ? 0 : undefined }, renderList));
};
//# sourceMappingURL=CarouselListDefault.js.map